import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';

const Resources = () => {
  const { user } = useContext(AuthContext);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    fetchResources();
  }, [selectedType, selectedCategory]);

  const fetchResources = async () => {
    try {
      const params = {};
      if (selectedType) params.resource_type = selectedType;
      if (selectedCategory) params.category = selectedCategory;
      if (searchTerm) params.search = searchTerm;

      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await axios.get(`${backendUrl}/api/resources`, { params });
      setResources(response.data);
    } catch (error) {
      console.error('Error fetching resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchResources();
  };

  const handleResourceAccess = async (resource) => {
    try {
      // Update views count
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.post(`${backendUrl}/api/resources/${resource.id}/view`);
      
      // Update local state to reflect the view
      setResources(prevResources => 
        prevResources.map(r => 
          r.id === resource.id 
            ? { ...r, views_count: r.views_count + 1 }
            : r
        )
      );

      // Open the content URL using a method that works in sandboxed environments
      if (resource.content_url) {
        // Create a temporary anchor element to handle the navigation
        const link = document.createElement('a');
        link.href = resource.content_url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        
        // Add to document, click, and remove
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // If no content URL, show a message
        alert('Content not available for this resource.');
      }
    } catch (error) {
      console.error('Error accessing resource:', error);
      // Still try to open the URL even if view count update fails
      if (resource.content_url) {
        try {
          // Fallback: try window.location as last resort
          const link = document.createElement('a');
          link.href = resource.content_url;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } catch (linkError) {
          // If all else fails, navigate in the same window
          console.warn('Cannot open in new tab, opening in same window:', linkError);
          window.location.href = resource.content_url;
        }
      }
    }
  };

  const likeResource = async (resourceId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await axios.post(`${backendUrl}/api/resources/${resourceId}/like`);
      // Update the resource likes count optimistically
      setResources(resources.map(resource => 
        resource.id === resourceId 
          ? { ...resource, likes_count: resource.likes_count + 1 }
          : resource
      ));
    } catch (error) {
      console.error('Error liking resource:', error);
    }
  };

  const getResourceIcon = (type) => {
    switch (type) {
      case 'video': return '📹';
      case 'article': return '📄';
      case 'blog': return '📝';
      case 'ebook': return '📖';
      default: return '📚';
    }
  };

  const getResourceTypeColor = (type) => {
    switch (type) {
      case 'video': return 'bg-red-100 text-red-800';
      case 'article': return 'bg-blue-100 text-blue-800';
      case 'blog': return 'bg-green-100 text-green-800';
      case 'ebook': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-300 rounded w-1/4"></div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow-sm p-6">
                <div className="h-6 bg-gray-300 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
                <div className="h-4 bg-gray-300 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Learning Resources</h1>
        <p className="text-gray-600">Educational content to help grow your business knowledge</p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500"
              />
              <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
            </div>
          </div>

          {/* Type Filter */}
          <div>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">All Types</option>
              <option value="video">📹 Video</option>
              <option value="article">📄 Article</option>
              <option value="blog">📝 Blog</option>
              <option value="ebook">📖 Ebook</option>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="">All Categories</option>
              <option value="Business Management">Business Management</option>
              <option value="Legal & Compliance">Legal & Compliance</option>
              <option value="Marketing">Marketing</option>
              <option value="Finance">Finance</option>
              <option value="Procurement">Procurement</option>
              <option value="Technology">Technology</option>
            </select>
          </div>
        </div>
      </div>

      {/* Resources Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {resources.length === 0 ? (
          <div className="col-span-full bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500 text-lg mb-4">No resources found</p>
            <p className="text-gray-400">Try adjusting your search criteria</p>
          </div>
        ) : (
          resources.map((resource) => (
            <div key={resource.id} className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              {/* Resource Header */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getResourceTypeColor(resource.resource_type)}`}>
                    {getResourceIcon(resource.resource_type)} {resource.resource_type.charAt(0).toUpperCase() + resource.resource_type.slice(1)}
                  </span>
                  <div className="text-sm text-gray-500">
                    {resource.category}
                  </div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 mb-3 line-clamp-2">
                  {resource.title}
                </h3>

                <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                  {resource.description}
                </p>

                {/* Author */}
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center text-sm font-medium">
                    {resource.author_name.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{resource.author_name}</p>
                    <p className="text-xs text-gray-500">{resource.author_organization}</p>
                  </div>
                </div>

                {/* Metadata */}
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <div className="flex items-center space-x-4">
                    <span>👁️ {resource.views_count}</span>
                    <span>❤️ {resource.likes_count}</span>
                  </div>
                  <div>
                    {resource.duration_minutes && (
                      <span>⏱️ {formatDuration(resource.duration_minutes)}</span>
                    )}
                    {resource.read_time_minutes && (
                      <span>📖 {resource.read_time_minutes} min read</span>
                    )}
                  </div>
                </div>

                {/* Tags */}
                {resource.tags && resource.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {resource.tags.slice(0, 3).map((tag, index) => (
                      <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Actions */}
                <div className="flex space-x-3">
                  {resource.resource_type === 'video' ? (
                    <button 
                      onClick={() => handleResourceAccess(resource)}
                      className="flex-1 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!resource.content_url}
                      title={resource.content_url ? 'Watch this video' : 'Content not available'}
                    >
                      🎥 Watch Now
                    </button>
                  ) : (
                    <button 
                      onClick={() => handleResourceAccess(resource)}
                      className="flex-1 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!resource.content_url}
                      title={resource.content_url ? 'Read this resource' : 'Content not available'}
                    >
                      📖 Read More
                    </button>
                  )}
                  <button
                    onClick={() => likeResource(resource.id)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    title="Like this resource"
                  >
                    ❤️
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Resources;